import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from "@mui/material/CircularProgress";
import classNames from "classnames";
import React from "react";
import {color, font, radius, shadow, state, type} from "common/tokens";

// Button roles. Pick by the job the button does, not by how it should look.
//
//   primary    the one action a surface is asking for — accent fill.
//              At most one per surface.
//   secondary  a supporting action next to a primary, or an action that should
//              not compete for attention. Surface fill with a border.
//              "Message group" and "View all events" are secondary.
//   danger     destructive, or an overdue action that needs urgency.
//   info       joining a live call. Tertiary fill; reserved for calls.
//   challenge  starting a quick challenge inside a session step. Purple while
//              the challenge is optional, tertiary blue once `required` says it
//              is mandatory, so the weight of the ask reads from the button.
//
// `tone="info"` tints a secondary's label for call-related actions.
// Sizes: default for in-card actions, `control` for row actions, `large` for
// page-level actions, `dense` for a compact bar that should not take height
// from the content it sits under — the session step footer.
//
// `dimmed` fades a button that has already been acted on but stays usable —
// a challenge that was taken and can be retaken.
//
// While `loading` is set the spinner replaces the label inside the button and
// the press stays blocked, so an action never needs a screen-level loader and
// the layout around it does not shift.
const useStyles = makeStyles({
  button: {
    ...type.chip,
    fontWeight: font.BOLD,
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 24px',
    border: '1px solid transparent',
    borderRadius: radius.CTA,
    cursor: 'pointer',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
  },
  fullWidth: {
    width: '100%',
  },
  primary: {
    background: color.ACCENT,
    color: color.ON_ACCENT,
    boxShadow: shadow.CTA,
  },
  secondary: {
    background: color.SURFACE,
    borderColor: color.CARD_BORDER,
    color: color.TEXT,
  },
  danger: {
    background: color.DANGER,
    color: color.ON_DANGER,
    boxShadow: shadow.CTA,
  },
  info: {
    background: color.TERTIARY,
    color: color.ON_TERTIARY,
  },
  toneInfo: {
    color: color.TERTIARY,
  },
  challenge: {
    background: color.PURPLE,
    color: color.ON_PURPLE,
  },
  challengeRequired: {
    background: color.TERTIARY,
    color: color.ON_TERTIARY,
  },
  dimmed: {
    opacity: state.DISABLED_OPACITY,
  },
  control: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    borderRadius: radius.CONTROL,
    padding: '12px 24px',
  },
  large: {
    ...type.body,
    fontWeight: font.SEMIBOLD,
    borderRadius: radius.CONTROL,
    padding: '12px 24px',
    minHeight: 48,
  },
  dense: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    borderRadius: radius.CONTROL,
    padding: '8px 18px',
  },
  spinner: {
    color: 'currentColor',
  },
  disabled: {
    background: color.DIVIDER,
    color: color.TEXT_SUBTLE,
    borderColor: 'transparent',
    boxShadow: 'none',
    cursor: 'default',
  },
});

const CtaButton = ({
  label,
  onClick,
  type = 'button',
  variant = 'primary',
  tone,
  size = 'cta',
  required = false,
  dimmed = false,
  fullWidth = false,
  disabled = false,
  loading = false,
  icon,
  iconAfter,
  className,
}) => {
  const classes = useStyles();
  const inactive = disabled || loading;

  return (
    <button
      type={type}
      onClick={inactive ? undefined : onClick}
      disabled={inactive}
      aria-busy={loading || undefined}
      className={classNames(
        classes.button,
        classes[variant],
        variant === 'challenge' && required && classes.challengeRequired,
        size === 'control' && classes.control,
        size === 'large' && classes.large,
        size === 'dense' && classes.dense,
        tone === 'info' && classes.toneInfo,
        fullWidth && classes.fullWidth,
        dimmed && !inactive && classes.dimmed,
        inactive && classes.disabled,
        className
      )}
    >
      {loading ? (
        <CircularProgress size={16} className={classes.spinner}/>
      ) : (
        <>
          {icon}
          {label}
          {iconAfter}
        </>
      )}
    </button>
  );
};

export default CtaButton;
